import React, { useEffect, useCallback } from "react";
import { Form, Row, Col, message } from "antd";
import {
  useQuery,
  useMutation,
  ApolloError,
  isApolloError,
} from "@apollo/client";
import moment from "moment";
import { useTranslation } from "react-i18next";

// components
import GET_PROFILE from "graphql/queries/GetProfile";
import UPDATE_PROFILE from "graphql/mutations/UpdateProfile";
import { getCredentials, storeCredentials } from "utils/auth";
import * as StyledForm from "pages/account/AccountFormStyles";
import UploadAvatar from "./UploadAvatar";
import { StyledButton } from "./UploadAvatarStyles";

const parseFormValues = (values: any) => ({
  ...values,
  birthDate: values.birthDate ? moment(values.birthDate) : null,
});

const MyProfileForm = () => {
  const { t } = useTranslation("my_profile");

  const [form] = Form.useForm();
  const { data, loading } = useQuery(GET_PROFILE, {
    fetchPolicy: "no-cache",
  });
  const [updateProfile] = useMutation(UPDATE_PROFILE, {
    onCompleted: () => {
      const newEmail = form.getFieldsValue().email;
      if (newEmail) {
        const oldCredentials = getCredentials();
        const newCredentials = { ...oldCredentials, uid: newEmail };
        storeCredentials(newCredentials);
      }
      message.success(t("Changes saved successfully!"));
    },
    onError: (e) => {
      const errors = e?.graphQLErrors?.map((error: any) => error?.message);
      if (errors) {
        message.error(errors);
      }
    },
  });

  // Set form default values
  useEffect(() => {
    if (data?.me) {
      // Need to parse any date field from string to Moment object
      form.setFieldsValue(parseFormValues(data?.me));
    }
  }, [data, form]);

  const onSubmitForm = useCallback(async () => {
    try {
      const { data: profileResponse } = await updateProfile({
        variables: {
          input: {
            attributes: form.getFieldsValue(),
          },
        },
      });

      const credentials = profileResponse?.updateMe?.credentials;
      if (credentials) {
        storeCredentials(credentials);
      } else {
        console.error("Unexpected profile response", profileResponse);
      }
    } catch (res: unknown) {
      if (isApolloError(res as Error)) {
        const errors = (res as ApolloError)?.graphQLErrors?.map(
          (error) => error.message
        );
        if (errors) {
          message.error(errors);
        }
      }
    }
  }, [form, updateProfile]);

  // Renderers
  const renderProfilePhotoGroup = () => (
    <Row>
      <UploadAvatar originalSrc={data?.me.image} />
    </Row>
  );

  const renderPersonalDataFormGroup = () => (
    <>
      <Row>
        <Col sm={{ span: 24, offset: 0 }} md={{ span: 6, offset: 0 }}>
          <StyledForm.Item
            name="givenName"
            label={t("My Given Name")}
            rules={[
              { required: true, message: t("Please input your given name!") },
            ]}
          >
            <StyledForm.Input onBlur={onSubmitForm} />
          </StyledForm.Item>
        </Col>
        <Col sm={{ span: 24, offset: 0 }} md={{ span: 6, offset: 1 }}>
          <StyledForm.Item
            name="familyName"
            label={t("My Last Name")}
            rules={[
              { required: true, message: t("Please input your family name!") },
            ]}
          >
            <StyledForm.Input onBlur={onSubmitForm} />
          </StyledForm.Item>
        </Col>
      </Row>
      <br />
      <Row>
        <Col sm={{ span: 24, offset: 0 }} md={{ span: 6, offset: 0 }}>
          <StyledForm.Item name="birthDate" label={t("My Birth Date")}>
            <StyledForm.DatePicker
              disabledDate={(date) => date && date > moment()}
              format="LL"
              onBlur={onSubmitForm}
            />
          </StyledForm.Item>
        </Col>
        <Col sm={{ span: 24, offset: 0 }} md={{ span: 6, offset: 1 }}>
          <StyledForm.Item name="placeLivingIn" label={t("Place I live in")}>
            <StyledForm.Select
              showSearch
              loading={loading}
              onBlur={onSubmitForm}
            >
              {data?.placesLivingIn.map((place: { name: string }) => (
                <StyledForm.Select.Option value={place.name} key={place.name}>
                  {place.name}
                </StyledForm.Select.Option>
              ))}
            </StyledForm.Select>
          </StyledForm.Item>
        </Col>
      </Row>
      <br />
      <Row>
        <Col sm={24} md={13}>
          <StyledForm.Item
            name="email"
            label="Email"
            rules={[{ required: true, message: t("Please input your email!") }]}
          >
            <StyledForm.Input
              placeholder={t("*Email")}
              type="email"
              name="email"
              onBlur={onSubmitForm}
            />
          </StyledForm.Item>
        </Col>
      </Row>
      <br />
    </>
  );

  const renderLanguageLearningFormGroup = () => (
    <>
      <Row>
        <Col sm={24} md={13}>
          <StyledForm.Item name="motherTongues" label={t("My mother tongue")}>
            <StyledForm.Select
              showSearch
              mode="multiple"
              showArrow
              loading={loading}
              onBlur={onSubmitForm}
            >
              {data?.profileLanguages.map((language: { language: string }) => (
                <StyledForm.Select.Option
                  value={language.language}
                  key={language.language}
                >
                  {language.language}
                </StyledForm.Select.Option>
              ))}
            </StyledForm.Select>
          </StyledForm.Item>
        </Col>
      </Row>
      <br />
      <Row>
        <Col sm={24} md={13}>
          <StyledForm.Item
            name="iAmStudying"
            label={t("I am studying these languages")}
          >
            <StyledForm.Select
              showSearch
              mode="multiple"
              showArrow
              loading={loading}
              onBlur={onSubmitForm}
            >
              {data?.profileLanguages.map((language: { language: string }) => (
                <StyledForm.Select.Option
                  value={language.language}
                  key={language.language}
                >
                  {language.language}
                </StyledForm.Select.Option>
              ))}
            </StyledForm.Select>
          </StyledForm.Item>
        </Col>
      </Row>
      <br />
      <Row>
        <Col sm={24} md={13}>
          <StyledForm.Item
            name="iWantToStudy"
            label={t("I want to study these languages")}
          >
            <StyledForm.Select
              showSearch
              mode="multiple"
              showArrow
              loading={loading}
              onBlur={onSubmitForm}
            >
              {data?.profileLanguages.map((language: { language: string }) => (
                <StyledForm.Select.Option
                  value={language.language}
                  key={language.language}
                >
                  {language.language}
                </StyledForm.Select.Option>
              ))}
            </StyledForm.Select>
          </StyledForm.Item>
        </Col>
      </Row>
    </>
  );

  const renderBioFormGroup = () => (
    <Row>
      <Col sm={24} md={13}>
        <StyledForm.Item name="bio">
          <StyledForm.Input.TextArea
            rows={4}
            placeholder={t("About me")}
            onBlur={onSubmitForm}
          />
        </StyledForm.Item>
      </Col>
    </Row>
  );

  return (
    <StyledForm.Container>
      <Form name="updateProfile" form={form} layout="vertical">
        {/* PROFILE PHOTO */}
        <StyledForm.Group>
          <StyledForm.GroupLabel>{t("PROFILE PHOTO")}</StyledForm.GroupLabel>
          <StyledForm.Content>{renderProfilePhotoGroup()}</StyledForm.Content>
        </StyledForm.Group>

        {/* PERSONAL DATA */}
        <StyledForm.Group>
          <StyledForm.GroupLabel>{t("PERSONAL DATA")}</StyledForm.GroupLabel>
          <StyledForm.Content>
            {renderPersonalDataFormGroup()}
          </StyledForm.Content>
        </StyledForm.Group>

        {/* LANGUAGE LEARNING */}
        <StyledForm.Group>
          <StyledForm.GroupLabel>
            {t("LANGUAGE LEARNING")}
          </StyledForm.GroupLabel>
          <StyledForm.Content>
            {renderLanguageLearningFormGroup()}
          </StyledForm.Content>
        </StyledForm.Group>

        {/* BIO */}
        <StyledForm.Group>
          <StyledForm.GroupLabel>{t("MY BIO")}</StyledForm.GroupLabel>
          <StyledForm.Content>{renderBioFormGroup()}</StyledForm.Content>
        </StyledForm.Group>

        {/* SUBMIT */}
        <StyledButton htmlType="submit">{t("SAVE CHANGES")}</StyledButton>
      </Form>
    </StyledForm.Container>
  );
};

export default MyProfileForm;
